import {
    FetchState,
    PartnerContentState
} from "@/shared/types";
import { PartnerContentType } from "@/admin/types";
import * as storeHelper from "@/shared/storeHelper";
import * as userService from "@/shared/api/userService";
import { defineStore } from "pinia";
import _ from "lodash";

const defaultPartnerContent: Record<PartnerContentType, string> = {
    drivewealthInvestmentPromo: "",
    footer: "",
    postLoginFooter: ""
};

export const initialState: PartnerContentState = {
    partnerId: 0,
    partnerContent: defaultPartnerContent,
    userPartnerContent: null,
    partnerFooterContent: null,
    partnerPostLoginFooterContent: null,
    partnerContentFetchState: FetchState.Uninitialized,
    userPartnerContentFetchState: FetchState.Uninitialized,
    partnerFooterContentFetchState: FetchState.Uninitialized,
    partnerPostLoginFooterContentFetchState: FetchState.Uninitialized,
};
export const usePartnerContentStore = defineStore("partnerContent", {
    state: (): PartnerContentState => (_.cloneDeep(initialState)),
    getters: {
        isLoadedContent: state => state.partnerContentFetchState === FetchState.Loaded,
        isLoadingContent: state => state.partnerContentFetchState === FetchState.Loading,
        isLoadedUserContent: state => state.userPartnerContentFetchState === FetchState.Loaded,
        isLoadingUserContent: state => state.userPartnerContentFetchState === FetchState.Loading,
        isLoadedPartnerFooterContent: state => state.partnerFooterContentFetchState === FetchState.Loaded,
        isLoadingPartnerFooterContent: state => state.partnerFooterContentFetchState === FetchState.Loading,
        isLoadedPartnerPostLoginFooterContent: state => state.partnerPostLoginFooterContentFetchState ===
            FetchState.Loaded,
        isLoadingPartnerPostLoginFooterContent: state => state.partnerPostLoginFooterContentFetchState ===
            FetchState.Loading
    },
    actions: {
        setPartner (partner: number) {
            this.partnerId = partner;
        },
        setFetchState (fetchState: FetchState) {
            this.partnerContentFetchState = fetchState;
        },
        setPartnerContent (partnerContent: Record<PartnerContentType, string>) {
            this.partnerContent = partnerContent;
        },
        setUserFetchState (fetchState: FetchState) {
            this.userPartnerContentFetchState = fetchState;
        },
        setUserPartnerContent (partnerContent: string) {
            this.userPartnerContent = partnerContent;
        },
        setPartnerFooterContent (content: string) {
            this.partnerFooterContent = content;
        },
        setPartnerPostLoginFooterContent (content: string) {
            this.partnerPostLoginFooterContent = content;
        },
        setPartnerFooterContentFetchState (fetchState: FetchState) {
            this.partnerFooterContentFetchState = fetchState;
        },
        setPartnerPostLoginFooterContentFetchState (fetchState: FetchState) {
            this.partnerPostLoginFooterContentFetchState = fetchState;
        },
        getPartnerContent (partnerId: number) {
            if (this.partnerContentFetchState !== FetchState.Uninitialized && this.partnerId === partnerId) {
                return;
            }
            this.setFetchState(FetchState.Loading);
            return userService.getPartnerContent(partnerId)
                .then(response => {
                    this.setPartnerContent(response.data);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        getUserPartnerContent (contentType: PartnerContentType) {
            if (this.userPartnerContentFetchState !== FetchState.Uninitialized) {
                return;
            }
            this.setUserFetchState(FetchState.Loading);
            return userService.getUserPartnerContent(contentType)
                .then(response => {
                    this.setUserPartnerContent(response.data);
                    this.setUserFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        getPartnerFooterContent (partnerId: number) {
            if (this.partnerFooterContentFetchState !== FetchState.Uninitialized && this.partnerId === partnerId) {
                return;
            }

            this.setPartnerFooterContentFetchState(FetchState.Loading);

            return userService.getPartnerContentDisplay(partnerId, PartnerContentType.Footer)
                .then(response => {
                    this.setPartnerFooterContent(response.data);
                    this.setPartner(partnerId);
                    this.setPartnerFooterContentFetchState(FetchState.Loaded);
                })
                .catch(error => {
                    return storeHelper.handleError({ error, rethrow: true });
                });
        },
        getPartnerPostLoginFooterContent () {
            if (this.partnerPostLoginFooterContentFetchState !== FetchState.Uninitialized) {
                return;
            }

            this.setPartnerPostLoginFooterContentFetchState(FetchState.Loading);

            return userService.getUserPartnerContent(PartnerContentType.PostLoginFooter)
                .then(response => {
                    this.setPartnerPostLoginFooterContent(response.data);
                    this.setPartnerPostLoginFooterContentFetchState(FetchState.Loaded);
                })
                .catch(error => {
                    return storeHelper.handleError({ error, rethrow: true });
                });
        },
        savePartnerContent (
            args: { partnerId: number, content: Record<PartnerContentType, string> }) {
            if (this.partnerContentFetchState === FetchState.Loading) {
                return;
            }
            this.setFetchState(FetchState.Loading);
            return userService.savePartnerContent(args.partnerId, args.content)
                .then(() => {
                    this.setPartnerContent(args.content);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        }
    }
});